// components/VideoGenerationStatus.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const VideoGenerationStatus = ({ userId, buttonColor = '#3b82f6' }) => {
  const [videoStatus, setVideoStatus] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);

  // Fetch video status
  const fetchVideoStatus = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(
        `/api/language/video-status/${userId}/${selectedLanguage}`
      );
      
      if (response.data.success) {
        setVideoStatus(response.data);
        setLastUpdate(new Date());
      } else {
        setError(response.data.message || 'Failed to fetch video status');
      }
    } catch (error) {
      console.error('Error fetching video status:', error);
      
      let errorMessage = 'Failed to load video status';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Video status endpoint not found';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while fetching video status';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [userId, selectedLanguage]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    fetchVideoStatus();

    if (autoRefresh) {
      const interval = setInterval(fetchVideoStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [fetchVideoStatus, autoRefresh]);

  // Get unique languages from videos
  const getAvailableLanguages = () => {
    if (!videoStatus?.videos) return [];
    const languages = [...new Set(videoStatus.videos.map(v => v.language))];
    return languages.sort();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'processing': return '#f59e0b';
      case 'pending': return '#6b7280';
      case 'failed': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'fa-check-circle';
      case 'processing': return 'fa-spinner fa-spin';
      case 'pending': return 'fa-clock';
      case 'failed': return 'fa-exclamation-circle';
      default: return 'fa-question-circle';
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
        color: '#718096'
      }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '12px' }}></i>
        Loading video status...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)',
          borderRadius: '16px',
          padding: '0',
          border: '2px solid #ff6b6b',
          boxShadow: '0 8px 32px rgba(220, 53, 69, 0.2)',
          overflow: 'hidden'
        }}>
          {/* Error Icon Header */}
          <div style={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
            padding: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}>
              <span style={{ fontSize: '48px' }}>⚠️</span>
            </div>
          </div>

          {/* Error Content */}
          <div style={{
            padding: '32px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#dc3545',
              margin: '0 0 16px 0',
              letterSpacing: '-0.5px'
            }}>
              Failed to Load Video Status
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#495057',
              lineHeight: 1.6,
              margin: '0 0 24px 0',
              fontWeight: 500
            }}>
              {error}
            </p>

            {/* Actions */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              padding: '0 24px'
            }}>
              <button
                onClick={fetchVideoStatus}
                style={{
                  flex: 1,
                  maxWidth: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '14px 24px',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <span style={{ fontSize: '18px' }}>🔄</span>
                <span>Try Again</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!videoStatus) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: '#718096'
      }}>
        <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}></i>
        <p>Unable to load video status</p>
      </div>
    );
  }

  const { stats, cronStats, videos } = videoStatus;
  const availableLanguages = getAvailableLanguages();
  const failedVideos = videos.filter(v => v.status === 'failed');
  const hasFailedVideos = failedVideos.length > 0;

  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{
            margin: '0 0 8px 0',
            color: '#2d3748',
            fontSize: '24px',
            fontWeight: 600
          }}>
            <i className="fas fa-video" style={{ marginRight: '12px', color: buttonColor }}></i>
            Video Generation Status
          </h2>
          <p style={{
            margin: '0',
            color: '#718096',
            fontSize: '14px'
          }}>
            Real-time monitoring of avatar video generation
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Last Update Info */}
          {lastUpdate && (
            <div style={{
              fontSize: '12px',
              color: '#718096',
              padding: '6px 12px',
              background: '#f8f9fa',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <i className="fas fa-clock"></i>
              <span>Updated: {lastUpdate.toLocaleTimeString()}</span>
            </div>
          )}

          {/* Auto-refresh toggle */}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#4a5568',
            padding: '6px 12px',
            background: autoRefresh ? '#e6f7ff' : '#f8f9fa',
            borderRadius: '8px',
            border: `2px solid ${autoRefresh ? buttonColor : '#e2e8f0'}`,
            transition: 'all 0.2s'
          }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <i className={`fas ${autoRefresh ? 'fa-sync fa-spin' : 'fa-sync'}`} style={{ fontSize: '12px' }}></i>
            Auto-refresh
          </label>

          {/* Manual refresh button */}
          <button
            onClick={fetchVideoStatus}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              border: `2px solid ${buttonColor}`,
              borderRadius: '8px',
              color: buttonColor,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = buttonColor;
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = buttonColor;
            }}
          >
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Error Alert Banner */}
      {hasFailedVideos && (
        <div style={{
          marginBottom: '24px',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)',
          border: '2px solid #ff6b6b',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(220, 53, 69, 0.15)',
          animation: 'slideIn 0.4s ease-out'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#dc2626',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              !
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#dc2626',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{failedVideos.length} Video Generation{failedVideos.length !== 1 ? 's' : ''} Failed</span>
              </div>
              <div style={{
                fontSize: '14px',
                color: '#721c24',
                lineHeight: '1.6',
                marginBottom: '12px'
              }}>
                Some videos failed to generate. Common issues include invalid API keys, insufficient credits, or rate limits. 
                Scroll down to see detailed error messages for each failed video.
              </div>
              
              {/* Quick Error Summary */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {(() => {
                  const errorTypes = {};
                  failedVideos.forEach(v => {
                    const type = v.errorType || 'UNKNOWN_ERROR';
                    errorTypes[type] = (errorTypes[type] || 0) + 1;
                  });
                  return Object.entries(errorTypes).map(([type, count]) => (
                    <div
                      key={type}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        background: 'white',
                        borderRadius: '6px',
                        fontSize: '11px',
                        border: '1px solid #ffcdd2',
                        fontWeight: 600
                      }}
                    >
                      <span style={{ color: '#6c757d' }}>{type.replace(/_/g, ' ')}:</span>
                      <span style={{ color: '#dc2626' }}>{count}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '30px'
      }}>
        {/* Total Videos */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: buttonColor, marginBottom: '8px' }}>
            {stats.total}
          </div>
          <div style={{ fontSize: '14px', color: '#718096' }}>Total Videos</div>
        </div>

        {/* Completed */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981', marginBottom: '8px' }}>
            {stats.completed}
          </div>
          <div style={{ fontSize: '14px', color: '#718096' }}>Completed</div>
        </div>

        {/* Processing */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '8px' }}>
            {stats.processing}
          </div>
          <div style={{ fontSize: '14px', color: '#718096' }}>Processing</div>
        </div>

        {/* Pending */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#6b7280', marginBottom: '8px' }}>
            {stats.pending}
          </div>
          <div style={{ fontSize: '14px', color: '#718096' }}>Pending</div>
        </div>

        {/* Failed */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ef4444', marginBottom: '8px' }}>
            {stats.failed}
          </div>
          <div style={{ fontSize: '14px', color: '#718096' }}>Failed</div>
        </div>
      </div>

      {/* Cron Job Status */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #e9ecef',
        marginBottom: '30px'
      }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '16px',
          fontWeight: 600,
          color: '#2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className="fas fa-robot" style={{ color: buttonColor }}></i>
          Background Service Status
        </h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Service Status</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: cronStats.isRunning ? '#10b981' : '#ef4444'
            }}>
              <i className={`fas ${cronStats.isRunning ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
              {cronStats.isRunning ? 'Running' : 'Stopped'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Last Check</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
              {cronStats.lastRun ? new Date(cronStats.lastRun).toLocaleTimeString() : 'Never'}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Total Checked</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
              {cronStats.totalChecked}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Total Updated</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
              {cronStats.totalUpdated}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>Downloaded</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
              {cronStats.totalDownloaded}
            </div>
          </div>
        </div>
      </div>

      {/* Language Filter */}
      {availableLanguages.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#4a5568'
          }}>
            Filter by Language
          </label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '2px solid #e2e8f0',
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none',
              minWidth: '200px'
            }}
          >
            <option value="all">All Languages</option>
            {availableLanguages.map(lang => (
              <option key={lang} value={lang}>{lang.toUpperCase()}</option>
            ))}
          </select>
        </div>
      )}

      {/* Videos List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#2d3748'
          }}>
            Video Generation Queue
          </h3>
          {lastUpdate && (
            <span style={{ fontSize: '12px', color: '#718096' }}>
              Last updated: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>

        {videos.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#718096'
          }}>
            <i className="fas fa-inbox" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}></i>
            <p style={{ margin: 0 }}>No videos found</p>
          </div>
        ) : (
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {videos.map((video, index) => (
              <div
                key={video._id}
                style={{
                  padding: '16px 20px',
                  borderBottom: index < videos.length - 1 ? '1px solid #e2e8f0' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '16px',
                  alignItems: 'start'
                }}>
                  <div>
                    {/* Video Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#2d3748'
                      }}>
                        {video.language.toUpperCase()}
                      </span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: `${getStatusColor(video.status)}20`,
                        color: getStatusColor(video.status),
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <i className={`fas ${getStatusIcon(video.status)}`}></i>
                        {video.status.toUpperCase()}
                      </span>
                      {video.downloadStatus === 'completed' && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: '#10b98120',
                          color: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <i className="fas fa-cloud-download-alt"></i>
                          DOWNLOADED
                        </span>
                      )}
                    </div>

                    {/* Text */}
                    <div style={{
                      fontSize: '13px',
                      color: '#4a5568',
                      marginBottom: '8px',
                      lineHeight: '1.5'
                    }}>
                      {video.translatedText}
                    </div>

                    {/* Meta Info */}
                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      flexWrap: 'wrap',
                      fontSize: '12px',
                      color: '#718096'
                    }}>
                      <span>
                        <i className="fas fa-clock" style={{ marginRight: '4px' }}></i>
                        {formatDate(video.createdAt)}
                      </span>
                      {video.duration > 0 && (
                        <span>
                          <i className="fas fa-hourglass-half" style={{ marginRight: '4px' }}></i>
                          {formatDuration(video.duration)}
                        </span>
                      )}
                      <span>
                        <i className="fas fa-fingerprint" style={{ marginRight: '4px' }}></i>
                        {video.videoId.substring(0, 12)}...
                      </span>
                    </div>

                    {/* Error Message */}
                    {video.error && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)',
                        border: '2px solid #ffcdd2',
                        borderRadius: '10px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: '#dc2626',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: '14px'
                          }}>
                            ⚠️
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: 600,
                              color: '#dc2626',
                              marginBottom: '4px',
                              fontSize: '13px'
                            }}>
                              Generation Failed
                            </div>
                            <div style={{
                              color: '#721c24',
                              lineHeight: '1.5',
                              fontSize: '12px'
                            }}>
                              {video.error}
                            </div>
                            
                            {/* Error Details */}
                            {video.errorDetails && (
                              <details style={{ marginTop: '8px' }}>
                                <summary style={{
                                  cursor: 'pointer',
                                  padding: '6px 10px',
                                  background: '#f8f9fa',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#495057',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  userSelect: 'none'
                                }}>
                                  <span>🔍</span>
                                  <span>View Technical Details</span>
                                </summary>
                                <div style={{
                                  marginTop: '8px',
                                  padding: '10px',
                                  background: '#f8f9fa',
                                  borderRadius: '6px',
                                  borderLeft: '3px solid #dc2626',
                                  fontSize: '11px',
                                  fontFamily: 'monospace',
                                  color: '#495057',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {video.errorDetails}
                                </div>
                              </details>
                            )}

                            {/* Error Type Badge */}
                            {video.errorType && (
                              <div style={{
                                marginTop: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                background: 'white',
                                borderRadius: '6px',
                                fontSize: '10px',
                                border: '1px solid #ffcdd2'
                              }}>
                                <span style={{ color: '#6c757d', fontWeight: 600 }}>Error Type:</span>
                                <span style={{ color: '#dc2626', fontWeight: 700, textTransform: 'capitalize' }}>
                                  {video.errorType.replace(/_/g, ' ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Video Preview */}
                  {video.status === 'completed' && video.videoUrl && (
                    <div style={{
                      width: '120px',
                      height: '68px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '2px solid #e2e8f0'
                    }}>
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          alt="Video thumbnail"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af'
                        }}>
                          <i className="fas fa-video" style={{ fontSize: '24px' }}></i>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add CSS for icons */}
      <style jsx>{`
        @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>
    </div>
  );
};

export default VideoGenerationStatus;
